import { ImageUploaderComponent, ValidImageResult } from '../../../Design/image-uploader/image-uploader.component';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  CategoriesService,
  CategoryDto,
} from '../../../services/categories/categories.service';
import { SnackbarService } from '../../../services/design/snackbar.service';

export interface Category {
  id: number;
  name: string;
  products: Category[];
  icon?: string;
  status: 'Active' | 'Inactive';
  active: boolean;
  position: number;
}
@Component({
  selector: 'app-categories',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ImageUploaderComponent],
  templateUrl: './add-categories.component.html',
  styleUrls: ['./add-categories.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddCategoriesComponent implements OnInit {
  private categoriesService = inject(CategoriesService);
  private snackbar = inject(SnackbarService);
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);

  @Input() modalData!: any | null;
  closeWithResult!: (result: any) => void;

  isEditMode = false;
  loading = false;
  hierarchyLoaded = false;

  selectedIconFile: File | null = null;
  iconUrl: string | null = null;

  categoryForm = this.fb.nonNullable.group({
    name: ['', Validators.required],
    position: [0, [Validators.required, Validators.min(0)]],
    parentCategoryId: [''],
    parentId: [''],
    moveParentId: [''],
  });

  categories: Category[] = [];
  childCategories: Category[] = [];

  ngOnInit() {
    this.isEditMode = !!this.modalData?.id;
    this.fetchCategories();

    if (this.modalData?.iconUrl) {
      this.iconUrl = this.modalData.iconUrl;
    }
  }

  // ---------------------------
  // FETCH CATEGORIES
  // ---------------------------
  fetchCategories() {
    this.categoriesService
      .getCategoryTreeAll(!this.isEditMode)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.categories = this.mapApiToUi(data);
          this.hierarchyLoaded = true;
          if (this.isEditMode && this.modalData) {
            this.populateFormForEdit();
          }
        },
        error: () => this.snackbar.error('Failed to load categories'),
      });
  }

  mapApiToUi(data: CategoryDto[]): any[] {
    return data.map((cat) => ({
      id: cat.id,
      name: cat.name,
      icon: cat.iconUrl,
      parentId: cat.parentId,
      position: cat.position,
      active: cat.isActive,
      products: cat.children ? this.mapApiToUi(cat.children) : [],
    }));
  }

  // ---------------------------
  // ICON HANDLING
  // ---------------------------
  onFilesValid(validFiles: ValidImageResult[]) {
    if (validFiles.length > 0) {
      this.selectedIconFile = validFiles[0].file;
    }
  }

  removeIcon() {
    this.selectedIconFile = null;
    this.iconUrl = null;
  }

  // ---------------------------
  // HIERARCHY
  // ---------------------------
  onParentChange() {
    const parentId = Number(this.categoryForm.value.parentCategoryId);
    const parent = this.categories.find((c) => c.id === parentId);
    this.childCategories = parent?.products ?? [];
    this.categoryForm.patchValue({ parentId: '' });
  }

  getHierarchyLabel(): string {
    if (!this.modalData?.parentId) return 'Top level category';

    const parent = this.categories.find((c) =>
      c.products.some((p) => p.id === this.modalData!.parentId)
    );

    if (parent) {
      const child = parent.products.find(
        (p) => p.id === this.modalData!.parentId
      );
      return `${parent.name} > ${child?.name}`;
    }

    return 'Top level category';
  }

  populateFormForEdit() {
    this.categoryForm.patchValue({
      name: this.modalData!.name,
      position: this.modalData!.position ?? 0,
      moveParentId: this.modalData!.parentId == null ? '' : String(this.modalData!.parentId),
    });
  }

  // ---------------------------
  // SUBMIT
  // ---------------------------
  submit() {
    if (this.categoryForm.invalid || this.loading || !this.hierarchyLoaded) return;

    this.loading = true;

    const raw = this.categoryForm.getRawValue();

    // Build payload
    const payload: any = {
      name: raw.name,
      position: Number(raw.position),
    };

    // Only include parentId in create mode
    if (!this.isEditMode) {
      payload.parentId = raw.parentId
        ? Number(raw.parentId)
        : raw.parentCategoryId
        ? Number(raw.parentCategoryId)
        : null;
      payload.level = raw.parentId ? 2 : raw.parentCategoryId ? 1 : 0;
    } else {
      payload.parentId = raw.moveParentId ? Number(raw.moveParentId) : null;
      payload.expectedParentId = this.modalData!.parentId ?? null;
    }

    const request$ = this.isEditMode
      ? this.categoriesService.updateCategory(this.modalData!.id, payload)
      : this.categoriesService.createCategory(payload);

    request$.subscribe({
      next: (category) => {
        if (this.isEditMode) this.modalData = { ...this.modalData, parentId: category.parentId ?? null };
        if (this.selectedIconFile) {
          this.uploadIcon(category.id);
        } else {
          this.onSuccess();
        }
      },
      error: (err) => {
        this.snackbar.error(err?.message || 'Operation failed');
        this.loading = false;
      },
    });
  }

  private findNode(nodes: Category[], id: number): Category | undefined {
    for (const node of nodes) {
      if (node.id === id) return node;
      const found = this.findNode(node.products, id);
      if (found) return found;
    }
    return undefined;
  }

  get movingSubcategoryCount(): number {
    const node = this.findNode(this.categories, this.modalData?.id);
    const count = (item: Category): number => 1 + item.products.reduce((sum, child) => sum + count(child), 0);
    return node ? count(node) - 1 : 0;
  }

  get moveParentOptions(): { id: number; label: string; disabled: boolean }[] {
    const node = this.findNode(this.categories, this.modalData?.id);
    const depth = (item: Category): number => item.products.length ? 1 + Math.max(...item.products.map(depth)) : 0;
    const subtree = new Set<number>();
    const collect = (item: Category) => { subtree.add(item.id); item.products.forEach(collect); };
    if (node) collect(node);
    const relativeDepth = node ? depth(node) : 0;
    const flatten = (items: Category[], level = 0, path = ''): { id: number; label: string; disabled: boolean }[] => items.flatMap(item => {
      const label = path ? `${path} > ${item.name}` : item.name;
      return [{ id: item.id, label, disabled: subtree.has(item.id) || !item.active || level + 1 + relativeDepth > 2 }, ...flatten(item.products, level + 1, label)];
    });
    return flatten(this.categories);
  }

  uploadIcon(categoryId: number) {
    this.categoriesService
      .uploadCategoryIcon(categoryId, this.selectedIconFile!)
      .subscribe({
        next: () => this.onSuccess(),
        error: () => {
          this.snackbar.error('Icon upload failed');
          this.loading = false;
        },
      });
  }

  onSuccess() {
    this.snackbar.success(
      this.isEditMode
        ? 'Category updated successfully'
        : 'Category created successfully'
    );
    this.cancelAndReset();
  }

  cancelAndReset() {
    this.categoryForm.reset();
    this.selectedIconFile = null;
    this.iconUrl = null;
    this.closeWithResult(null);
  }
}
